import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";

type PolicyVersion = Database["public"]["Tables"]["policy_versions"]["Row"];
type PolicyVersionInsert = Database["public"]["Tables"]["policy_versions"]["Insert"];
type PolicyVersionUpdate = Database["public"]["Tables"]["policy_versions"]["Update"];

interface UsePolicyVersionsParams {
  policyType?: "privacy_policy" | "terms_acceptance" | "cookies" | "marketing";
  status?: "draft" | "active" | "archived";
}

interface UsePolicyVersionsResult {
  data: PolicyVersion[] | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch list of policy versions
 */
export function usePolicyVersions(
  params?: UsePolicyVersionsParams
): UsePolicyVersionsResult {
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["policy-versions", params?.policyType, params?.status],
    queryFn: async () => {
      let query = supabase
        .from("policy_versions")
        .select("*")
        .order("created_at", { ascending: false });

      if (params?.policyType) {
        query = query.eq("policy_type", params.policyType);
      }

      if (params?.status) {
        query = query.eq("status", params.status);
      }

      const { data, error } = await query;

      if (error) {
        throw new Error(error.message || "Failed to fetch policy versions");
      }

      return data as PolicyVersion[];
    },
  });

  return {
    data,
    isLoading,
    error: error as Error | null,
    refetch,
  };
}

/**
 * Hook to fetch a single policy version
 */
export function usePolicyVersion(id: string | null) {
  return useQuery({
    queryKey: ["policy-version", id],
    queryFn: async () => {
      if (!id) return null;

      const { data, error } = await supabase
        .from("policy_versions")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        throw new Error(error.message || "Failed to fetch policy version");
      }

      return data as PolicyVersion;
    },
    enabled: !!id,
  });
}

/**
 * Hook to create a new policy version
 */
export function useCreatePolicyVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Omit<PolicyVersionInsert, "id" | "created_at" | "updated_at" | "created_by" | "published_at">) => {
      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error("Authentication required");
      }

      const { data: result, error } = await supabase
        .from("policy_versions")
        .insert({
          ...data,
          created_by: user.id,
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) {
        throw new Error(error.message || "Failed to create policy version");
      }

      return result as PolicyVersion;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["policy-versions"] });
    },
  });
}

/**
 * Hook to update a policy version
 */
export function useUpdatePolicyVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<PolicyVersionUpdate> }) => {
      const { data: result, error } = await supabase
        .from("policy_versions")
        .update({
          ...data,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select()
        .single();

      if (error) {
        throw new Error(error.message || "Failed to update policy version");
      }

      return result as PolicyVersion;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["policy-versions"] });
      queryClient.invalidateQueries({ queryKey: ["policy-version", variables.id] });
    },
  });
}

/**
 * Hook to delete a policy version
 */
export function useDeletePolicyVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("policy_versions")
        .delete()
        .eq("id", id);

      if (error) {
        throw new Error(error.message || "Failed to delete policy version");
      }

      return { id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["policy-versions"] });
    },
  });
}

/**
 * Hook to publish a policy version
 */
export function usePublishPolicyVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      // First, get the policy version to be published
      const { data: versionToPublish, error: fetchError } = await supabase
        .from("policy_versions")
        .select("*")
        .eq("id", id)
        .single();

      if (fetchError) {
        throw new Error(fetchError.message || "Failed to fetch policy version");
      }

      // Archive any currently active versions of the same policy type
      const { error: archiveError } = await supabase
        .from("policy_versions")
        .update({ status: "archived", updated_at: new Date().toISOString() })
        .eq("policy_type", versionToPublish.policy_type)
        .eq("status", "active");

      if (archiveError) {
        throw new Error(archiveError.message || "Failed to archive previous versions");
      }

      // Publish the selected version
      const { data: result, error: publishError } = await supabase
        .from("policy_versions")
        .update({
          status: "active",
          published_at: new Date().toISOString(),
          effective_date: versionToPublish.effective_date || new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select()
        .single();

      if (publishError) {
        throw new Error(publishError.message || "Failed to publish policy version");
      }

      return result as PolicyVersion;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["policy-versions"] });
    },
  });
}

/**
 * Hook to get consent statistics per version
 */
export function usePolicyVersionStats(params?: UsePolicyVersionsParams) {
  return useQuery({
    queryKey: ["policy-version-stats", params?.policyType],
    queryFn: async () => {
      let query = supabase
        .from("consent_logs")
        .select("policy_version")
        .eq("granted", true);

      if (params?.policyType) {
        query = query.eq("consent_type", params.policyType);
      }

      const { data, error } = await query;

      if (error) {
        throw new Error(error.message || "Failed to fetch statistics");
      }

      // Count consents per version
      const versionCounts = new Map<string, number>();
      data?.forEach((log) => {
        const count = versionCounts.get(log.policy_version) || 0;
        versionCounts.set(log.policy_version, count + 1);
      });

      // Convert to array format
      return Array.from(versionCounts.entries()).map(([version, consent_count]) => ({
        version,
        consent_count,
      }));
    },
  });
}

